import React from 'react';
import { ExternalLinkIcon, PinIcon, PinOffIcon, XIcon } from 'lucide-react';
import { AgentMark } from '../../ui/AgentMark';
import { LevelBadge } from '../../ui/LevelBadge';
import { ProcessingTag } from '../../ui/ProcessingTag';
import { WhyPopover } from '../../ui/WhyPopover';
import { getAgent } from '../../../utils/agents';
import type { ActionLevel, AgentId, Processing } from '../../../types/anna';
interface StagePanelProps {
  agent: AgentId;
  kicker: string;
  title: string;
  level?: ActionLevel;
  where: Processing;
  traceId: string;
  pinned: boolean;
  onPin: () => void;
  onDismiss: () => void;
  onOpenBeheer: () => void;
  footerExtra?: React.ReactNode;
  children: React.ReactNode;
}
const iconBtn = 'grid h-9 w-9 place-items-center rounded-full text-ink-muted transition-colors duration-150 hover:bg-white/[0.08] hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60';

/** Large stage panel: pin, dismiss (returns into Anna) or open in Beheer. */
export function StagePanel({
  agent,
  kicker,
  title,
  level,
  where,
  traceId,
  pinned,
  onPin,
  onDismiss,
  onOpenBeheer,
  footerExtra,
  children
}: StagePanelProps) {
  const a = getAgent(agent);
  return <article className="glass flex flex-col rounded-3xl p-5 2xl:p-6" data-id="element-755" data-mp="32ai3up4vze3t">
      <header className="flex items-start gap-3" data-id="element-756" data-mp="37acyn3rs8nu5">
        <AgentMark agentId={agent} data-id="element-757" data-mp="37c7ew4s3zrrw" />
        <div className="min-w-0 flex-1" data-id="element-758" data-mp="3bc7v9uyrqd1l">
          <p className="text-[13px] text-ink-muted" data-id="element-759" data-mp="3bbgbdheja3r5">
            <span className="font-medium text-ink-soft" data-id="element-760" data-mp="3gbb0bfer0j9k">{a.name}</span> · {kicker}
          </p>
          <h2 className="mt-0.5 text-[20px] font-semibold leading-tight text-ink" data-id="element-761" data-mp="3l2178x2tou6d">{title}</h2>
        </div>
        <div className="flex items-center gap-0.5" data-id="element-762" data-mp="3q149yavbyob6">
          <button type="button" onClick={onPin} aria-pressed={pinned} aria-label={pinned ? 'Losmaken' : 'Vastzetten'} className={`${iconBtn} ${pinned ? 'text-accent' : ''}`} data-id="element-763" data-mp="3q0kur1x2ee0u">
            {pinned ? <PinOffIcon className="h-4 w-4" data-id="element-764" data-mp="2x7pes6iob7y" /> : <PinIcon className="h-4 w-4" data-id="element-765" data-mp="2ubt18abhulm" />}
          </button>
          <button type="button" onClick={onOpenBeheer} aria-label="Openen in Beheer" className={iconBtn} data-id="element-766" data-mp="1zxu1lluqth5q">
            <ExternalLinkIcon className="h-4 w-4" data-id="element-767" data-mp="1uz93brz1x7jx" />
          </button>
          <button type="button" onClick={onDismiss} aria-label="Terug naar Anna" className={iconBtn} data-id="element-768" data-mp="28pvvt2e7y17k">
            <XIcon className="h-4 w-4" data-id="element-769" data-mp="23ydwsjvraap8" />
          </button>
        </div>
      </header>
      <div className="mt-4 text-[15px] leading-relaxed text-ink-soft" data-id="element-770" data-mp="2in0o7jynqswt">{children}</div>
      <footer className="mt-5 flex flex-wrap items-center gap-2 border-t border-white/[0.07] pt-4" data-id="element-771" data-mp="2dofmfg3rc169">
        {level && <LevelBadge level={level} data-id="element-772" data-mp="2rnylljelc26f" />}
        <ProcessingTag where={where} data-id="element-773" data-mp="2rmx8zahxf7ml" />
        {footerExtra}
        <span className="ml-auto" data-id="element-774" data-mp="2nmwj8tpdna08">
          <WhyPopover traceId={traceId} data-id="element-775" data-mp="2nny41pw73av5" />
        </span>
      </footer>
    </article>;
}